/**
 * I6: the "วันนี้" panel (key 0, or the HUD menu button): plots, work, training boost, and the
 * keeper's daily lines (camp, world boss, events, duel buff). Reads state only.
 */
import { BOOST_SOURCES, CROPS, OFFLINE_JOBS, TRAIN_BOOST, dailyLinesOf, jobOf, smallDungeonState, worldBossOf, type World } from '@nine/shared';
import { goButton, type Send } from './goButton';
import '../loading/loading.css';
import './today.css';
import { h } from './dom';
import { lsCard } from './card';
import { breakthroughControl } from './qiPool';
import { codexRows } from './codex';

const SRC_TEXT: Record<string, string> = { place: 'ที่ฝึก', dance: 'รำหมู่', sectDance: 'รำสำนัก', duel: 'ท้าประลอง', item: 'ยา/ของ' };

const mmss = (s: number): string => {
  const m = Math.ceil(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)} ชม. ${m % 60} นาที` : `${m} นาที`;
};

export class TodayPanel {
  private readonly card = lsCard({ id: 'today', title: 'วันนี้', key: '0', menuId: 'mbToday', refreshEvery: 1, draw: (body) => this.draw(body) });

  constructor(
    private readonly world: World,
    private readonly send?: Send,
  ) {}

  get isOpen(): boolean {
    return this.card.isOpen;
  }
  toggle(): void {
    this.card.toggle();
  }
  open(): void {
    this.card.open();
  }
  close(): void {
    this.card.close();
  }
  sync(dt: number): void {
    this.card.sync(dt);
  }

  private section(title: string, lines: string[]): HTMLElement[] {
    return [h('h4', {}, title), h('ul', {}, ...lines.map((l) => h('li', {}, l.replace(/^• /, ''))))];
  }

  /** J7: "ไป" rows — the camp, the world boss, the keeper of the day's events. */
  private goRows(): HTMLElement[] {
    const w = this.world;
    const rows: HTMLElement[] = [];
    const add = (text: string, target: Parameters<typeof goButton>[1]) =>
      rows.push(h('li', {}, text, goButton(this.send, target)));
    const camp = smallDungeonState(w).find((c) => !c.cleared);
    if (camp) add(`ค่าย ${camp.name}`, { kind: 'point', x: camp.x, y: camp.y });
    const wb = worldBossOf(w)?.state();
    if (wb && !wb.over) add('บอสโลก', { kind: 'point', x: wb.arena.x, y: wb.arena.y });
    for (const e of w.entities.values())
      if (e.kind === 'npc' && e.title === 'ผู้ดูแลกิจกรรม') add(`${e.name} (รำสำนัก/กิจกรรม)`, { kind: 'npc', id: e.id });
    return rows.length ? [h('h4', {}, 'ไปที่'), h('ul', {}, ...rows)] : [];
  }

  private draw(body: HTMLElement): void {
    const w = this.world;
    const pr = w.progress;
    const plots = Object.values(pr.trades.plots).map((p) => {
      const c = CROPS[p.crop];
      if (!c) return '';
      const left = Math.max(0, c.grow - p.grown);
      return `${c.name}: ${left <= 0 ? 'สุกแล้ว' : `อีก ${mmss(left)}`}`;
    });
    const job = OFFLINE_JOBS[jobOf(pr)];
    const boost = w.sys.cultivation.boost;
    const src = BOOST_SOURCES.filter((s) => boost.bonus(s) > 0).map((s) => `${SRC_TEXT[s] ?? s} +${Math.round(boost.bonus(s) * 100)}%`);
    const capLeft = Math.max(0, Math.round(TRAIN_BOOST.softCap - boost.boosted));
    body.replaceChildren(
      ...((c) => (c ? [h('h4', {}, 'ทะลวงขั้น'), c] : []))(breakthroughControl(w, this.send)),
      ...this.section('แปลงปลูก', plots.filter(Boolean).length ? plots.filter(Boolean) : ['ยังไม่ได้ปลูกอะไร']),
      ...this.section('งานตอนไม่อยู่', [job ? job.name : 'ยังไม่ได้เลือกงาน']),
      ...this.section('ฝึกเพิ่ม', [src.length ? src.join(' · ') : 'ยังไม่มี', `โควตาเต็มอัตราเหลือ ${capLeft} แต้ม`]),
      ...this.section('กิจกรรมวันนี้', dailyLinesOf(w)),
      ...this.goRows(),
      ...codexRows(w),
    );
  }
}
